import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { RootState } from '../../../store/store';
import ShellSaac, { useVoltar } from '../../../components/shell/ShellSaac';
import PageHeader from '../../../ui/composicao/PageHeader';
import Card from '../../../ui/primitivos/Card';
import Icone, { NomeIcone } from '../../../ui/primitivos/Icone';
import { HOME } from '../../home/routes';

import ModalEnvioDeAssociado from '../../../components/modal/ti/ModalEnvioDeAssociado';
import ModalEnvioDeVeiculo from '../../../components/modal/ti/ModalEnvioDeVeiculo';
import ModalRateioValorCota from '../../../components/modal/ti/ModalRateioValorCota';
import ModalSincronizarCotas from '../../../components/modal/ti/ModalSincronizarCotas';
import ModalSincronizarProdutos from '../../../components/modal/ti/ModalSincronizarProdutos';
import ModalSincronizarRegionaisCooperativas from '../../../components/modal/ti/ModalSincronizarRegionaisCooperativas';
import ModalDescobertaAssociados from '../../../components/modal/ti/ModalDescobertaAssociados';
import ModalBaseFipe from '../../../components/modal/ti/ModalBaseFipe';
import {
  TIENVIOS,
  TIUSUARIOS,
  TITERMOS,
  TICRONS,
  TILOGS,
  TIPLANOS,
  TIBIKES,
  TICONTRATOS,
  TIEXPURGO,
} from '../routes';

/**
 * Administração (TI) — o hub das 16 ferramentas administrativas.
 * Reescrito sobre o design system na at-67 (frente D/D5).
 *
 * 📌 **A Home é o molde desta tela, e sempre foi:** a versão anterior importava
 * `views/home/pages/home.css` e usava as classes `.home-menu-*` dela, com um
 * comentário dizendo *"a tela usa o mesmo visual da Home nova"*. A at-55 reescreveu
 * a Home no design system e **deixou aquele CSS para trás** — de 08/09/2026 até
 * esta branch, `/ti` era o **único importador** de `home.css` no app inteiro (e a
 * única consumidora das `.home-menu-*`). Agora as duas usam o mesmo `Card`.
 *
 * 🔴 **As opções estão agrupadas em 5 seções** (decisão de Victor, 11/09/2026). São
 * 16 itens: em lista plana eles ocupam ~1.000 px de rolagem no celular e a ordem
 * intercalava assuntos — "Contratos" entre "Termos" e "Planos", "Base FIPE" no fim,
 * depois das sincronizações. O agrupamento é só um `<h2>` por seção; nenhum
 * componente novo.
 *
 * ⚠️ **`OPCOES_TI` continua sendo a fonte desta tela, e não `menuOpcoes.ts`**: são
 * ferramentas que **não** vão ao menu global (o drawer mostra "Administração (TI)"
 * como uma opção só). O que mudou é que o ícone agora é um `NomeIcone` do kit, e
 * não um `import` de `.svg` — inline ele herda a cor do contexto, que é o que
 * permite o item acompanhar o estado do card (com `<img>` era impossível).
 */

/** Qual modal da tela está aberto (null = nenhum). */
type ModalTI =
  | 'ENVIO_ASSOCIADO'
  | 'ENVIO_VEICULO'
  | 'RATEIO'
  | 'SYNC_COTAS'
  | 'SYNC_PRODUTOS'
  | 'SYNC_REGIONAIS_COOPERATIVAS'
  | 'DESCOBERTA_ASSOCIADOS'
  | 'BASE_FIPE';

interface OpcaoTI {
  id: string;
  icone: NomeIcone;
  titulo: string;
  subtitulo: string;
  /** Rota para navegar OU modal para abrir — exatamente um dos dois. */
  rota?: () => string;
  modal?: ModalTI;
  /**
   * Só para ADMIN (at-77). Todo o resto deste hub é TI/ADMIN — esta marca existe
   * para a **exclusão definitiva**, que é irreversível e apaga PII: o projeto
   * reserva a ADMIN o que tem esse peso (precedente do `change-password-emergency`).
   *
   * ⚠️ É só a visibilidade do card. Quem de fato barra é o backend, com 403 —
   * esconder na tela não é controle de acesso.
   */
  somenteAdmin?: boolean;
}

interface SecaoTI {
  id: string;
  titulo: string;
  opcoes: OpcaoTI[];
}

/**
 * As opções como dado (padrão da Home): um lugar só para título, subtítulo, ícone
 * e ação — o markup é um `map`.
 */
const SECOES_TI: SecaoTI[] = [
  {
    id: 'usuarios',
    titulo: 'Usuários',
    opcoes: [
      /*
       * at-68: eram DOIS cards ("Criar Usuário" e "Editar Usuário") apontando para
       * duas telas que declaravam o mesmo formulário. Viraram uma central só — criar
       * é um botão dentro dela.
       */
      {
        id: 'usuarios',
        icone: 'usuario-editar',
        titulo: 'Usuários',
        subtitulo: 'Cadastre, edite, redefina senhas, encerre sessões, desative e importe usuários',
        rota: TIUSUARIOS,
      },
    ],
  },
  {
    id: 'envio-sga',
    titulo: 'Envio ao SGA',
    opcoes: [
      {
        id: 'envio-associado',
        icone: 'enviar',
        titulo: 'Envio de Informações de Associado',
        subtitulo: 'Envie as informações de um associado para o SGA',
        modal: 'ENVIO_ASSOCIADO',
      },
      {
        id: 'envio-veiculo',
        icone: 'enviar',
        titulo: 'Envio de Informações de Veículo',
        subtitulo: 'Envie as informações de um veículo para o SGA',
        modal: 'ENVIO_VEICULO',
      },
      {
        id: 'acompanhamento-envios',
        icone: 'adesoes',
        titulo: 'Acompanhamento de Envios',
        subtitulo: 'Veja associados e veículos pendentes de envio ao SGA e envie por aqui',
        rota: TIENVIOS,
      },
      {
        id: 'descoberta-associados',
        icone: 'busca',
        titulo: 'Descoberta de Associados (SGA)',
        subtitulo: 'Traga para o SAAC os associados que foram criados direto no SGA',
        modal: 'DESCOBERTA_ASSOCIADOS',
      },
    ],
  },
  {
    id: 'documentos',
    titulo: 'Documentos e produtos',
    opcoes: [
      {
        id: 'catalogo-termos',
        icone: 'documento',
        titulo: 'Administração de Termos',
        subtitulo:
          'Cadastre tipos de termo, edite o texto das cláusulas, configure o envio por WhatsApp e veja as regras',
        rota: TITERMOS,
      },
      {
        id: 'contratos',
        icone: 'documento',
        titulo: 'Contratos',
        subtitulo: 'Acompanhe o que foi assinado, o que está pendente e faça distratos',
        rota: TICONTRATOS,
      },
      {
        id: 'planos-cotacao',
        icone: 'cotacoes',
        titulo: 'Planos de Cotação',
        subtitulo: 'Edite coberturas, assistências e benefícios que saem no PDF da cotação',
        rota: TIPLANOS,
      },
      {
        id: 'bikes-eletricas',
        icone: 'veiculo',
        titulo: 'Motocicletas Elétricas',
        subtitulo: 'Cadastre marcas, modelos e o valor de referência usado na adesão (sem FIPE)',
        rota: TIBIKES,
      },
    ],
  },
  {
    id: 'catalogos',
    titulo: 'Catálogos e sincronizações',
    opcoes: [
      {
        id: 'valor-rateio',
        icone: 'moeda',
        titulo: 'Valor do Rateio',
        subtitulo: 'Consulte e atualize o valor do rateio por cota da competência',
        modal: 'RATEIO',
      },
      {
        id: 'sync-cotas',
        icone: 'recarregar',
        titulo: 'Sincronizar Cotas',
        subtitulo: 'Atualize valores e situação das cotas a partir do SGA',
        modal: 'SYNC_COTAS',
      },
      {
        id: 'sync-produtos',
        icone: 'recarregar',
        titulo: 'Sincronizar Produtos',
        subtitulo: 'Atualize produtos e flags de obrigatoriedade a partir do SGA',
        modal: 'SYNC_PRODUTOS',
      },
      // at-40 (item 43): o card "Sincronizar Termos" SAIU. Com termos 100% SAAC, o catálogo
      // de tipos é nosso (tabela `termo_template`) — ressincronizar do SGA não teria efeito
      // sobre ele e daria a impressão falsa de que o SGA ainda governa os tipos. O que era
      // esse card virou o botão "Novo tipo de termo" em Administração de Termos.
      {
        id: 'sync-regionais-cooperativas',
        icone: 'recarregar',
        titulo: 'Sincronizar Regionais, Cooperativas e Tipos',
        subtitulo:
          'Atualize os espelhos de regionais, cooperativas e tipos de veículo a partir do SGA',
        modal: 'SYNC_REGIONAIS_COOPERATIVAS',
      },
    ],
  },
  {
    id: 'sistema',
    titulo: 'Sistema',
    opcoes: [
      {
        // at-85 (item 66): o log geral — o mecanismo do ADR 0062 do SOAC. TI/ADMIN.
        id: 'logs-sistema',
        icone: 'documento',
        titulo: 'Logs do Sistema',
        subtitulo: 'Veja quem fez o quê, quando e de onde — inclusive as tentativas recusadas',
        rota: TILOGS,
      },
      {
        id: 'crons-sistema',
        icone: 'relogio',
        titulo: 'Crons do Sistema',
        subtitulo: 'Veja todas as rotinas automáticas: agenda, o que fazem e última execução',
        rota: TICRONS,
      },
      {
        // Read-only: quem sincroniza a FIPE é o serviço fipe-atual (at-16), com painel
        // próprio no SOAC. Aqui o TI só vê em que mês está a base que o SAAC usa.
        id: 'base-fipe',
        icone: 'veiculo',
        titulo: 'Base FIPE',
        subtitulo: 'Veja o mês de referência da tabela FIPE que o SAAC usa e onde ela é sincronizada',
        modal: 'BASE_FIPE',
      },
      {
        // at-77 (item 64): a ÚNICA exceção ao "marca, nunca apaga" do projeto.
        // Existe porque o soft delete não resolve o caso do dado de teste — CPF,
        // RG, placa, chassi e renavam são únicos, então o registro marcado continua
        // bloqueando o recadastro.
        id: 'expurgo-associado',
        icone: 'fechar',
        titulo: 'Excluir Associado',
        subtitulo:
          'Apague em definitivo um cadastro de teste: o associado, os veículos, as fotos e os documentos',
        rota: TIEXPURGO,
        somenteAdmin: true,
      },
    ],
  },
];

const TIPage: React.FC = () => {
  const navigate = useNavigate();
  const voltar = useVoltar(HOME());
  const [modalAberto, setModalAberto] = useState<ModalTI | null>(null);
  const userType = useSelector((state: RootState) => state.auth.userType);

  /**
   * Esconde as opções que este papel não alcança e, com elas, a seção que ficaria
   * vazia — um título de seção sem nenhum card é ruído (at-77).
   */
  const secoesVisiveis = useMemo(
    () =>
      SECOES_TI.map((secao) => ({
        ...secao,
        opcoes: secao.opcoes.filter(
          (opcao) => !opcao.somenteAdmin || userType === 'ADMIN',
        ),
      })).filter((secao) => secao.opcoes.length > 0),
    [userType],
  );

  const abrirOpcao = (opcao: OpcaoTI) => {
    if (opcao.rota) {
      navigate(opcao.rota());
      return;
    }
    if (opcao.modal) setModalAberto(opcao.modal);
  };

  const fecharModal = () => setModalAberto(null);

  return (
    <ShellSaac cabecalho={<PageHeader titulo="Administração (TI)" aoVoltar={voltar} />}>
      <div className="tw-mx-auto tw-w-full tw-max-w-5xl tw-px-0 tw-pb-8 lg:tw-px-8 lg:tw-py-8">
        <nav aria-label="Menu de administração" className="tw-flex tw-flex-col tw-gap-6">
          {secoesVisiveis.map((secao) => (
            <section key={secao.id} aria-labelledby={`secao-${secao.id}`}>
              {/*
                ⚠️ **A cor do título TROCA com a faixa** (ajuste de Victor, 11/09/2026).
                O `AppShell` pinta o conteúdo de `tw-bg-brand` no mobile e `tw-bg-shell`
                no desktop — um cinza fixo (`texto-suave`) fica ilegível sobre o
                laranja. É o mesmo princípio que o `FaixaDaMarca` aplica ao `Botao`
                no cabeçalho, aqui resolvido por breakpoint porque o fundo é do shell,
                não de um contexto.
              */}
              <h2
                id={`secao-${secao.id}`}
                className="tw-mb-2 tw-mt-0 tw-px-4 tw-font-marca tw-text-sm tw-font-bold tw-uppercase tw-tracking-wider tw-text-white/90 lg:tw-px-0 lg:tw-text-texto-suave"
              >
                {secao.titulo}
              </h2>

              {/*
                UMA lista para as duas larguras (mesmo desenho da Home):
                • mobile  — card branco único, itens viram linhas separadas por borda
                • desktop — grade de cards independentes sobre o fundo neutro
              */}
              <ul className="tw-m-0 tw-list-none tw-overflow-hidden tw-rounded-2xl tw-bg-white tw-p-0 lg:tw-grid lg:tw-grid-cols-2 lg:tw-gap-4 lg:tw-overflow-visible lg:tw-rounded-none lg:tw-bg-transparent xl:tw-grid-cols-3">
                {secao.opcoes.map((opcao) => (
                  <li key={opcao.id}>
                    <Card
                      interativo
                      /* ⚠️ `aparencia="nenhuma"`: a superfície muda com a largura
                         (linha de lista no mobile, card no desktop) e sobrescrever
                         raio/borda por className não é confiável em Tailwind — ver
                         o comentário em `Card.tsx`. */
                      aparencia="nenhuma"
                      onClick={() => abrirOpcao(opcao)}
                      rotuloAcessivel={`${opcao.titulo}: ${opcao.subtitulo}`}
                      className="tw-flex tw-h-full tw-min-h-[62px] tw-items-center tw-gap-3 tw-border-b tw-border-superficie-borda tw-bg-white tw-px-4 tw-py-3 last:tw-border-b-0 lg:tw-min-h-0 lg:tw-flex-col lg:tw-items-start lg:tw-gap-4 lg:tw-rounded-2xl lg:tw-border lg:tw-p-6 lg:tw-shadow-sm"
                    >
                      <span className="tw-flex tw-h-10 tw-w-10 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-xl tw-bg-brand-tint tw-text-brand lg:tw-h-11 lg:tw-w-11">
                        <Icone nome={opcao.icone} tamanho={22} />
                      </span>

                      <span className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-0.5">
                        <span className="tw-font-marca tw-text-base tw-font-bold tw-leading-snug tw-text-texto-titulo">
                          {opcao.titulo}
                        </span>
                        <span className="tw-line-clamp-2 tw-text-xs tw-leading-snug tw-text-texto-suave lg:tw-text-sm">
                          {opcao.subtitulo}
                        </span>
                      </span>

                      {/* Afordância de clique do item inteiro. Some no desktop, onde
                          o card já tem hover e elevação próprios. */}
                      <span
                        className="tw-shrink-0 tw-text-texto-desabilitado lg:tw-hidden"
                        aria-hidden="true"
                      >
                        <Icone nome="chevron-direita" tamanho={18} />
                      </span>
                    </Card>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </nav>
      </div>

      <ModalEnvioDeAssociado isOpen={modalAberto === 'ENVIO_ASSOCIADO'} onClose={fecharModal} />
      <ModalEnvioDeVeiculo isOpen={modalAberto === 'ENVIO_VEICULO'} onClose={fecharModal} />
      <ModalRateioValorCota isOpen={modalAberto === 'RATEIO'} onClose={fecharModal} />
      <ModalSincronizarCotas isOpen={modalAberto === 'SYNC_COTAS'} onClose={fecharModal} />
      <ModalSincronizarProdutos isOpen={modalAberto === 'SYNC_PRODUTOS'} onClose={fecharModal} />
      <ModalSincronizarRegionaisCooperativas
        isOpen={modalAberto === 'SYNC_REGIONAIS_COOPERATIVAS'}
        onClose={fecharModal}
      />
      <ModalDescobertaAssociados
        isOpen={modalAberto === 'DESCOBERTA_ASSOCIADOS'}
        onClose={fecharModal}
      />
      <ModalBaseFipe isOpen={modalAberto === 'BASE_FIPE'} onClose={fecharModal} />
    </ShellSaac>
  );
};

export default TIPage;
